/* =====================================================================
   Zling — central client module
   ONE definition of metal tones + karat multipliers, consumed by both
   the product page selectors and the collection metal/karat toggle.
   (Solution Engine Gate 4: one central rule, never repeated per page.)
   The authoritative pricing multipliers live server-side in the
   `pricing_rule` metaobject and are printed into window.ZLING by
   layout/theme.liquid; the values below are only a safe fallback.
   ===================================================================== */
(function () {
  "use strict";

  var CFG = window.ZLING || {};
  var METALS = CFG.metals || {
    white:  { label: "White Gold",  a: "#C9CBD0", b: "#F2F3F5", line: "#9A9CA2" },
    yellow: { label: "Yellow Gold", a: "#C9A24B", b: "#E9CE7F", line: "#A9843F" },
    rose:   { label: "Rose Gold",   a: "#CE9078", b: "#EEC4B1", line: "#B5765E" }
  };
  var KARATS = CFG.karats || { "9": 0.82, "14": 1.00, "18": 1.24 };
  var CURRENCY = CFG.currency || "INR";

  // Indian ring size -> inner diameter (mm), from Tanishq's published ring-size
  // chart (staticimg.tanishq.co.in/sizing-guide/ring-size-guide.pdf). Zling has
  // no chart of its own and doesn't capture band diameter as product data, so
  // this is the same authoritative external source the scale-reference PDP
  // component uses to label a selected size — kept here, not duplicated in the
  // snippet, since ring size -> mm is reference data any future feature might
  // also need (Gate 4).
  var RING_SIZE_MM = {
    10: 15.90, 11: 16.30, 12: 16.50, 13: 16.90, 14: 17.30, 15: 17.50, 16: 17.90,
    17: 18.10, 18: 18.50, 19: 18.80, 20: 19.20, 21: 19.40, 22: 19.80, 23: 20.00, 24: 20.40
  };
  function ringSizeMM(size) {
    var n = parseInt(String(size).replace(/[^0-9]/g, ""), 10);
    return RING_SIZE_MM[n] || null;
  }

  function formatMoney(paise) {
    // Shopify prices are in the shop's minor units; render as ₹ with Indian grouping.
    var major = Math.round(paise) / 100;
    try {
      return new Intl.NumberFormat("en-IN", {
        style: "currency", currency: CURRENCY, maximumFractionDigits: 0
      }).format(major);
    } catch (e) {
      return "₹ " + major.toLocaleString("en-IN");
    }
  }

  function applyMetalTone(rootEl, metalKey) {
    var m = METALS[metalKey] || METALS.yellow;
    rootEl.style.setProperty("--metal", m.a);
    rootEl.style.setProperty("--metal-2", m.b);
    rootEl.setAttribute("data-metal", metalKey);
  }

  // Public surface (theme sections attach their handlers to these).
  window.Zling = {
    metals: METALS,
    karats: KARATS,
    formatMoney: formatMoney,
    ringSizeMM: ringSizeMM,
    metalTone: function (key) { return METALS[key] || METALS.yellow; },
    priceForKarat: function (base14, karat) {
      var mult = KARATS[String(karat)] != null ? KARATS[String(karat)] : 1;
      return Math.round(base14 * mult);
    },
    applyMetalTone: applyMetalTone
  };

  /* ---- product-card image sliders (hover to scrub through views) ---- */
  function initCardSliders() {
    document.querySelectorAll(".pcard-media[data-slider]").forEach(function (media) {
      if (media.dataset.init) return;
      media.dataset.init = "1";
      var track = media.querySelector(".pcard-track");
      var slides = media.querySelectorAll(".pcard-slide");
      var dots = media.querySelectorAll(".pcard-dots span");
      if (!track || slides.length < 2) return;
      var n = slides.length, cur = 0;
      function go(i) {
        i = Math.max(0, Math.min(n - 1, i));
        if (i === cur) return;
        cur = i;
        track.style.transform = "translateX(-" + (i * 100) + "%)";
        dots.forEach(function (d, k) { d.classList.toggle("on", k === i); });
      }
      // Touch devices scroll the track natively (see the (hover:none) block in
      // zling-base.css). Binding mousemove there is actively harmful: a tap emits
      // a synthetic mousemove that yanks the track to whichever slide sits under
      // the finger, fighting the swipe. So the two modes are mutually exclusive.
      var coarse = window.matchMedia &&
        window.matchMedia("(hover:none), (pointer:coarse), (max-width:900px)").matches;

      if (coarse) {
        var raf = 0;
        track.addEventListener("scroll", function () {
          if (raf) return;
          raf = requestAnimationFrame(function () {
            raf = 0;
            var i = Math.round(track.scrollLeft / track.clientWidth);
            i = Math.max(0, Math.min(n - 1, i));
            if (i !== cur) {
              cur = i;
              dots.forEach(function (d, k) { d.classList.toggle("on", k === i); });
            }
          });
        }, { passive: true });
        return;
      }

      media.addEventListener("mousemove", function (e) {
        var r = media.getBoundingClientRect();
        go(Math.floor(((e.clientX - r.left) / r.width) * n));
      });
      media.addEventListener("mouseleave", function () { go(0); });
    });
  }
  if (document.readyState !== "loading") initCardSliders();
  else document.addEventListener("DOMContentLoaded", initCardSliders);
  // re-init after collection metal toggle re-renders, etc.
  window.Zling.initCardSliders = initCardSliders;

  /* ---- ring-size options carry their mm diameter ----
     Competitor audit 2 Aug 2026: CaratLane labels every size button with its
     physical diameter ("12 / 51.8mm") — the one place a competitor communicates
     size better than we do. We already hold that lookup (RING_SIZE_MM above,
     powering the scale-reference overlay), so the label is free.

     Done in JS rather than in main-product.liquid deliberately: rendering it
     server-side would mean a SECOND copy of the size->mm table living in Liquid,
     which is exactly the drift Gate 4 exists to prevent. One table, two readers.

     Only the visible label changes — option.value stays "IN 14" verbatim, because
     it is posted to the cart as properties[Ring size] and is parsed by the
     scale-reference component. */
  function initRingSizeLabels() {
    document.querySelectorAll('select[name="properties[Ring size]"]').forEach(function (sel) {
      if (sel.dataset.mmLabelled) return;
      sel.dataset.mmLabelled = "1";
      Array.prototype.forEach.call(sel.options, function (opt) {
        var mm = ringSizeMM(opt.value);
        if (!mm) return;                       // "Select your size" placeholder
        var note = opt.textContent.split("—")[1];   // e.g. " most popular"
        opt.textContent = opt.value + " · " + mm.toFixed(2) + " mm" +
          (note ? " — " + note.trim() : "");
      });
    });
  }
  if (document.readyState !== "loading") initRingSizeLabels();
  else document.addEventListener("DOMContentLoaded", initRingSizeLabels);
  window.Zling.initRingSizeLabels = initRingSizeLabels;

  /* ---- card price re-format (₹, Indian grouping) ----
     The shop's own money_format is "Rs. {{amount}}" with Western digit
     grouping, so any price rendered via the plain `| money` filter (every
     product card, "you may also like", collection grids) reads
     "Rs. 143,550.00" while the PDP price reads "₹ 2,32,639" — same store,
     two formats. The PDP gets ₹ + Indian grouping only because its own JS
     overwrites the server-rendered text; cards had no such script. This is
     that script, scoped to [data-cents] so it never touches cart/checkout/
     email prices, which are Shopify's own templates outside this theme. */
  function formatCardPrices(root) {
    (root || document).querySelectorAll("[data-cents]").forEach(function (el) {
      var cents = parseInt(el.getAttribute("data-cents"), 10);
      if (!isNaN(cents)) el.textContent = formatMoney(cents);
    });
  }
  if (document.readyState !== "loading") formatCardPrices();
  else document.addEventListener("DOMContentLoaded", function () { formatCardPrices(); });
  window.Zling.formatCardPrices = formatCardPrices;
})();
